import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/restaurant-styles.css';
import { ItemLoading, MenuLoading } from '../../components/loading/Loading';
import { ProductContext } from './Context';
import { useNavigate, useParams } from 'react-router-dom';
import { getProductPrice } from '../../initialize/managers/Currency';
import { get, isEmpty, keyBy, startCase, uniqBy } from 'lodash';
import CartModal from '../../components/modal/CartModal';
import restaurantFallback from '../../assests/image/restaurant-image.png';
import { useLang } from '../../initialize/i18n/LangContext';
import { getTranslated, resolveShopLangs } from '../../initialize/i18n/lang';
import type { Lang } from '../../initialize/i18n/lang';
import { LangDropdown } from '../../initialize/i18n/LangDropdown';

/* ── Netlise N mark (inline, no white-bg rect) ──────── */
const NetliseMark = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="34 36 62 61" style={{ display: 'block', flexShrink: 0 }} aria-hidden="true">
    <g transform="translate(-1002 -277)">
      <g transform="translate(1039.749 314.949)">
        <g transform="translate(-2 0)" fill={color}>
          <rect width="18" height="39" transform="translate(0.251 18.051)" />
          <rect width="19" height="40" transform="translate(39.251 17.051)" />
          <path d="M-.2.01,40,0V18l-40.2.02Z" transform="translate(18.251 0.051)" />
          <path d="M672,311.138h17.919v-18.17Z" transform="translate(-671.749 -292.917)" />
        </g>
      </g>
    </g>
  </svg>
);

/* ── SVG icon helpers ───────────────────────────────── */
const ChevLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 6-6 6 6 6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const CloseIcon = ({ color = '#fff' }: { color?: string }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5h18M6 12h12M10 19h4" />
  </svg>
);

const PinIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
  </svg>
);

const StarIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="#FFD700">
    <path d="M12 2.5l2.9 6.4 7 .7-5.2 4.7 1.5 6.9L12 17.6l-6.2 3.6 1.5-6.9L2 9.6l7-.7Z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m4 12 5 5L20 6" />
  </svg>
);

/* ── Diet config ────────────────────────────────────── */
const DIET_LABELS: Record<string, { label: string; color: string }> = {
  v:  { label: 'Vegetarian', color: '#10B981' },
  vg: { label: 'Vegan',      color: '#059669' },
  gf: { label: 'Gluten-free',color: '#3B82F6' },
  s:  { label: 'Spicy',      color: '#EF4444' },
};

/* ── Product thumbnail ──────────────────────────────── */
const CDN = 'https://dm6mmpk56rvc9.cloudfront.net/';

const ItemThumb = ({ menuImage, alt }: { menuImage?: string; alt: string }) => (
  <div className="rv-item-thumb">
    <img
      src={menuImage ? `${CDN}${menuImage}` : ''}
      alt={alt}
      onError={(e) => {
        const t = e.target as HTMLImageElement;
        t.style.display = 'none';
      }}
    />
  </div>
);

/* ── Filter sheet ───────────────────────────────────── */
const FilterSheet = ({
  open,
  onClose,
  diet,
  setDiet,
}: {
  open: boolean;
  onClose: () => void;
  diet: string[];
  setDiet: (d: string[]) => void;
}) => {
  if (!open) return null;
  return (
    <>
      <div className="rv-filter-backdrop" onClick={onClose} />
      <div className="rv-filter-sheet">
        <div className="rv-filter-drag" />
        <div className="rv-filter-header">
          <div className="rv-filter-title">Filters</div>
          <button className="rv-filter-clear" onClick={() => setDiet([])}>
            Clear all
          </button>
        </div>
        <div className="rv-filter-section-label">Dietary</div>
        <div className="rv-filter-grid">
          {Object.entries(DIET_LABELS).map(([key, d]) => {
            const on = diet.includes(key);
            return (
              <button
                key={key}
                className={`rv-filter-tile${on ? ' on' : ''}`}
                onClick={() =>
                  setDiet(on ? diet.filter((x) => x !== key) : [...diet, key])
                }
              >
                <span className="rv-filter-tile-dot" style={{ background: d.color }} />
                <span className="rv-filter-tile-label">{d.label}</span>
                {on && <CheckIcon />}
              </button>
            );
          })}
        </div>
        <button className="rv-filter-apply" onClick={onClose}>
          Apply
        </button>
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════
   RestaurantView — hero image + pull-up card
══════════════════════════════════════════════════════ */
const RestaurantView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();

  /* ── local UI state ─────────────────────────────── */
  const [isOpen, setOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedMenu, setSelectedMenu] = useState<any>({});
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [dietFilters, setDietFilters] = useState<string[]>([]);

  /* ── Refs for parallax ──────────────────────────────── */
  const pageRef    = useRef<HTMLDivElement>(null);
  const heroImgRef = useRef<HTMLImageElement>(null);

  /* ── derived data state ─────────────────────────── */
  const [selectedShop, setSelectedShop] = useState<any>({});
  const [categoryMap, setCategoryMap] = useState<any>({});
  const [productMap, setProductMap] = useState<any>({});

  const {
    shop,
    menu,
    carts,
    products,
    fetchDataForQrId,
    loadingProduct,
    setSelectedProduct,
  } = useContext(ProductContext);

  /* ── shop hero image — CloudFront CDN ──────────────── */
  const heroImageUrl: string = shop.menuImage
    ? `${CDN}${shop.menuImage}`
    : restaurantFallback;

  useEffect(() => {
    if (params?.shopId) {
      fetchDataForQrId(params.shopId as string);
    }
  }, [params, navigate]);

  useEffect(() => {
    if (!isEmpty(shop)) setSelectedShop(shop);
  }, [shop]);

  useEffect(() => {
    if (!isEmpty(products)) {
      const filtered = products.filter(({ showMenu }: any) => showMenu);
      const availableCategories = uniqBy(
        filtered,
        (p: any) => p.categoryId
      ).map((p: any) => p.categoryResult);

      setCategoryMap(keyBy(availableCategories, 'categoryId'));
      setProductMap(keyBy(filtered, 'pid'));
    }
  }, [products]);

  useEffect(() => {
    if (!isEmpty(menu)) setSelectedMenu(menu[0]);
  }, [menu]);

  /* ── Parallax: hero image drifts at 35% of scroll speed ── */
  useEffect(() => {
    const el  = pageRef.current;
    const img = heroImgRef.current;
    if (!el || !img) return;
    const onScroll = () => {
      img.style.transform = `translateY(${el.scrollTop * 0.35}px)`;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  /* ── products in selected menu ──────────────────── */
  const allMenuProducts = useMemo<any[]>(() => {
    if (!selectedMenu?.categories || isEmpty(productMap)) return [];
    return selectedMenu.categories.flatMap((cat: any) =>
      (cat.products || [])
        .map(({ productId }: any) => productMap[productId])
        .filter(Boolean)
    );
  }, [selectedMenu, productMap]);

  /* ── supported languages from shop data ─────────── */
  const shopLangs = useMemo(
    () => resolveShopLangs(shop?.menuLanguages),
    [shop?.menuLanguages],
  );

  // Reset to 'en' if the current shop doesn't support the active lang
  useEffect(() => {
    if (shopLangs.length > 0 && !shopLangs.includes(lang)) {
      setLang('en');
    }
  }, [shopLangs]);

  /* ── search filter ──────────────────────────────── */
  const searchResults = useMemo<any[] | null>(() => {
    if (!searchText.trim()) return null;
    const q = searchText.trim().toLowerCase();
    return allMenuProducts.filter((p: any) => {
      if (p.productName?.toLowerCase().includes(q)) return true;
      if (p.translations) {
        return Object.values(p.translations).some(
          (t: any) => t?.productName?.toLowerCase().includes(q),
        );
      }
      return false;
    });
  }, [searchText, allMenuProducts]);

  /* ── diet filter ────────────────────────────────── */
  const applyDietFilter = (items: any[]) => {
    if (!dietFilters.length) return items;
    return items.filter((p: any) => {
      const pDiet: string[] = p.dietary || p.diet || [];
      return dietFilters.every((f) => pDiet.includes(f));
    });
  };

  /* ── navigate to product detail ─────────────────── */
  const onClickProduct = (product: any) => {
    const p = { ...product, quantity: 1, amount: product.itemSalePrice || product.itemPrice };
    setSelectedProduct(p);
    navigate(`${product.pid}`);
  };

  const shopName: string    = get(selectedShop, 'shopName', '');
  const rating: number|null = get(selectedShop, 'rating', null);
  const address: string     = get(selectedShop, 'address', '');
  const phone: string       = get(selectedShop, 'phoneNumber', '');

  /* shop.image → S3 public bucket (different from CloudFront CDN used for products) */
  const S3_BASE = 'https://netlise-image-bucket.s3.ap-southeast-1.amazonaws.com/public/';
  const shopLogoUrl: string | null = selectedShop?.image
    ? `${S3_BASE}${selectedShop.image}`
    : null;

  return (
    <div className="rv-page" ref={pageRef}>
      <Helmet>
        <title>{`Menu of ${shopName || ''} - Netlise Menu App`}</title>
        <meta
          name="description"
          content={`Drinks and Food menu of ${shopName} located at ${address}`}
        />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* ── Shop hero image ─────────────────────────── */}
      <div className="rv-hero">
        <img
          ref={heroImgRef}
          src={heroImageUrl}
          alt={shopName || 'Restaurant'}
          className="rv-hero-img"
        />
        {/* Gradient scrim so back button stays visible */}
        <div className="rv-hero-scrim" />

        {/* Floating back button */}
        <button
          className="rv-back-float"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ChevLeft />
        </button>

        {/* Language picker — mirrors back button position on the right */}
        <LangDropdown
          lang={lang}
          setLang={setLang}
          langs={shopLangs}
          className="rv-lang-float"
        />
      </div>

      {/* ── Pull-up white card ──────────────────────── */}
      <div className="rv-card">
        {/* Drag handle */}
        <div className="rv-drag-handle-wrap">
          <div className="rv-drag-handle" />
        </div>

        {/* Shop info — logo (if available) + name/rating/meta */}
        <div className="rv-shop-info">
          <div className="rv-shop-info-top">

            {/* Shop logo badge — uses shop.image from S3 public bucket */}
            {shopLogoUrl && (
              <div className="rv-shop-logo">
                <img
                  src={shopLogoUrl}
                  alt={shopName}
                  onError={(e) => {
                    /* hide the wrapper on broken image so layout stays clean */
                    const wrap = e.currentTarget.parentElement as HTMLElement;
                    if (wrap) wrap.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Text column: name row + meta row */}
            <div className="rv-shop-info-text">
              <div className="rv-shop-name-row">
                <div className="rv-shop-name">{shopName}</div>
                {rating != null && rating > 0 && (
                  <div className="rv-shop-rating">
                    <StarIcon />
                    <span>{rating}</span>
                  </div>
                )}
              </div>

              {(address || phone) && (
                <div className="rv-shop-meta">
                  {address && (
                    <span className="rv-shop-meta-item">
                      <PinIcon /> {address}
                    </span>
                  )}
                  {phone && (
                    <span className="rv-shop-meta-item">
                      <PhoneIcon /> {phone}
                    </span>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Sticky search + tabs — locks to viewport top once hero scrolls away */}
        <div className="rv-sticky-top">
          {/* Search + filter bar */}
          <div className="rv-search-bar">
            <div className="rv-search-pill">
              <SearchIcon />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search dishes"
                aria-label="Search dishes"
              />
              {searchText && (
                <button
                  className="rv-search-clear"
                  onClick={() => setSearchText('')}
                  aria-label="Clear search"
                >
                  <CloseIcon color="#374151" />
                </button>
              )}
            </div>
            <button
              className={`rv-filter-btn${dietFilters.length > 0 ? ' active' : ''}`}
              onClick={() => setShowFilters(true)}
              aria-label="Filters"
            >
              <FilterIcon />
              {dietFilters.length > 0 && (
                <span className="rv-filter-badge">{dietFilters.length}</span>
              )}
            </button>
          </div>

          {/* Category / menu tabs */}
          {!loadingProduct && !isEmpty(menu) && (
            <div className="rv-tabs-scroll" role="tablist">
              {menu.map((item: any) => {
                const active = selectedMenu?.menuId === item.menuId;
                return (
                  <button
                    key={item.menuId}
                    role="tab"
                    aria-selected={active}
                    className={`rv-tab ${active ? 'rv-tab-active' : 'rv-tab-inactive'}`}
                    onClick={() => {
                      setSelectedMenu(item);
                      setSearchText('');
                    }}
                  >
                    {item.title}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {loadingProduct && <MenuLoading />}

        {/* Scrollable product area */}
        <div className="rv-scroll">
          {/* Search results */}
          {searchResults !== null && (
            <>
              <div className="rv-section-header">
                <span className="rv-section-title">Results</span>
                <span className="rv-section-count">{searchResults.length} items</span>
              </div>
              {searchResults.length === 0 ? (
                <div className="rv-empty-state">
                  <div className="rv-empty-icon">◌</div>
                  <div className="rv-empty-text">No items match your search.</div>
                </div>
              ) : (
                applyDietFilter(searchResults).map((product: any) => (
                  <ProductCard
                    key={product.pid}
                    product={product}
                    lang={lang}
                    onClick={() => onClickProduct(product)}
                  />
                ))
              )}
            </>
          )}

          {/* Normal grouped view */}
          {searchResults === null &&
            !isEmpty(selectedMenu) &&
            !loadingProduct &&
            selectedMenu?.categories?.map((cat: any) => {
              const category = categoryMap[cat.categoryId];
              const catProducts = (cat.products || [])
                .map(({ productId }: any) => productMap[productId])
                .filter(Boolean);
              const displayed = applyDietFilter(catProducts);
              if (displayed.length === 0) return null;

              return (
                <div key={cat.categoryId}>
                  <div className="rv-section-header">
                    <span className="rv-section-title">{getTranslated(category, 'categoryName', lang) || ''}</span>
                    <span className="rv-section-count">{displayed.length} items</span>
                  </div>
                  {displayed.map((product: any) => (
                    <ProductCard
                      key={product.pid}
                      product={product}
                      lang={lang}
                      onClick={() => onClickProduct(product)}
                    />
                  ))}
                </div>
              );
            })}

          {loadingProduct && <ItemLoading />}

          {/* Footer */}
          <div className="rv-footer">
            <span>Powered by</span>
            <NetliseMark size={14} color="#6366F1" />
            <span className="rv-footer-wordmark">netlise</span>
          </div>
        </div>
      </div>

      {/* Filter sheet */}
      <FilterSheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        diet={dietFilters}
        setDiet={setDietFilters}
      />

      {/* Cart modal (preserved) */}
      <CartModal
        carts={carts}
        isOpen={isOpen}
        shop={selectedShop}
        onClose={() => setOpen(false)}
      />
    </div>
  );
};

/* ── Product card ───────────────────────────────────── */
const ProductCard = ({
  product,
  lang,
  onClick,
}: {
  product: any;
  lang: Lang;
  onClick: () => void;
}) => {
  const price = getProductPrice(product);
  const hasVariants = product?.priceVariants?.length > 0;
  const diet: string[] = product.dietary || product.diet || [];
  const displayName = getTranslated(product, 'productName', lang);
  const displayDesc = getTranslated(product, 'description', lang);

  return (
    <div className="rv-item-card" onClick={onClick}>
      <div className="rv-item-row">
        <ItemThumb menuImage={product.menuImage} alt={product.productName || ''} />
        <div className="rv-item-body">
          <div className="rv-item-name-row">
            <span className="rv-item-name">{lang === 'ru' ? displayName : startCase(product.productName)}</span>
          </div>
          {displayDesc && (
            <div className="rv-item-desc">{displayDesc}</div>
          )}
          <div className="rv-item-footer">
            <div className="rv-diet-row">
              {diet.map((flag: string) => {
                const d = DIET_LABELS[flag];
                if (!d) return null;
                return (
                  <span
                    key={flag}
                    className="rv-diet-pill"
                    style={{ background: d.color + '14', color: d.color }}
                  >
                    <span className="rv-diet-dot" style={{ background: d.color }} />
                    {d.label}
                  </span>
                );
              })}
            </div>
            <div className="rv-item-price">
              {hasVariants && <span className="rv-item-price-from">from </span>}
              {price}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantView;
